import {
  getAdminStats,
  getAllCards,
  getAllDeposits,
  getBIN,
  getCardBalance,
  getCardDetails,
  getCardInfo,
  getCardInfoAdmin,
  getCardPurchases,
  getCards,
  getCardTransactions,
  getCardTransactionsAdmin,
  getDeposit,
  getDeposits,
  getMerchant,
  getMerchantsStats,
  getOverview,
  getPendingCards,
  getRate,
  getRecentTransactions,
  getReferrals,
  getSettlements,
  getUser,
  getUserCards,
  getUserReferrals,
  getUsers,
  getWallets,
} from "@/lib/api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

export const useGetUser = () =>
  useQuery({
    queryKey: ["user"],
    queryFn: getUser,
  });

export const useGetMerchant = ({ id }: { id: string }) =>
  useQuery({
    queryKey: ["merchant"],
    queryFn: () => getMerchant(id),
  });

export const useGetRecentTransactions = () => {
  return useQuery({
    queryKey: ["recentTransactions"],
    queryFn: getRecentTransactions,
  });
};

export const useGetDeposits = ({
  page,
  limit,
}: {
  page: number;
  limit: number;
}) => {
  return useQuery({
    queryKey: ["deposits", page, limit],
    queryFn: () => getDeposits({ page, limit }),
  });
};

export const useGetCardTransactions = (filters) => {
  return useQuery({
    queryKey: ["userCardTransactions", { ...filters }],
    queryFn: () => getCardTransactions(filters),
    refetchOnMount: false,
  });
};

export const useGetDepositOrder = ({ id, enabled }) => {
  return useQuery({
    queryKey: ["depositOrder"],
    queryFn: () => getDeposit(id),
    refetchInterval: 1000,
    enabled: enabled,
  });
};

export const useGetCards = () => {
  return useQuery({
    queryKey: ["cards"],
    queryFn: getCards,
    refetchOnMount: false,
  });
};

export const useGetUserCards = (id) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: () => getUserCards(id),
    refetchOnMount: false,
  });
};

export const useGetCardInfoAdmin = (id) => {
  return useQuery({
    queryKey: ["allCards", id],
    queryFn: () => getCardInfoAdmin(id),
    refetchOnMount: false,
  });
};

export const useGetPendingCards = () => {
  return useQuery({
    queryKey: ["pendingCards"],
    queryFn: getPendingCards,
    refetchOnMount: false,
  });
};

export const useGetCardDetails = ({ id, enabled }) => {
  return useQuery({
    queryKey: ["cardDetails", id],
    queryFn: () => getCardDetails(id),
    enabled: enabled,
  });
};

export const useGetCardInfo = ({ id, enabled }) => {
  return useQuery({
    queryKey: ["cardInfo", id],
    queryFn: () => getCardInfo(id),
    enabled: enabled,
  });
};

export const useGetBIN = () => {
  return useQuery({
    queryKey: ["bins"],
    queryFn: getBIN,
  });
};

// Admin Dashboard Queries

export const useGetAdminStats = () => {
  return useQuery({
    queryKey: ["adminStats"],
    queryFn: getAdminStats,
  });
};

export const useGetMerchantStats = () => {
  return useQuery({
    queryKey: ["merchantStats"],
    queryFn: getMerchantsStats,
  });
};

export const useGetOverview = ({ startDate, endDate }) => {
  return useQuery({
    queryKey: ["overview", startDate, endDate],
    queryFn: () => getOverview({ startDate, endDate }),
  });
};

export const useGetUsers = (filters) => {
  return useQuery({
    queryKey: ["users", { ...filters }],
    queryFn: () => getUsers(filters),
  });
};

export const useGetSettlements = (filters) => {
  return useQuery({
    queryKey: ["settlements", { ...filters }],
    queryFn: () => getSettlements(filters),
  });
};

export const useGetWallets = (filters) => {
  return useQuery({
    queryKey: ["wallets", { ...filters }],
    queryFn: () => getWallets(filters),
  });
};

export const useGetAllCards = (filters) => {
  return useQuery({
    queryKey: ["allCards", { ...filters }],
    queryFn: () => getAllCards(filters),
  });
};

export const useGetCardPurchases = ({ id, filters }) => {
  return useQuery({
    queryKey: ["cardPurchases", { ...filters }],
    queryFn: () => getCardPurchases(id, filters),
  });
};

export const useGetCardBalance = (id) => {
  return useQuery({
    queryKey: ["cardBalance", id],
    queryFn: () => getCardBalance(id),
  });
};

export const useGetAllDeposits = (filters) => {
  return useQuery({
    queryKey: ["allDeposits", { ...filters }],
    queryFn: () => getAllDeposits(filters),
    placeholderData: keepPreviousData,
  });
};

export const useGetReferrals = (filters) => {
  return useQuery({
    queryKey: ["referrals", { ...filters }],
    queryFn: () => getReferrals(filters),
    placeholderData: keepPreviousData,
  });
};
export const useGetUserReferrals = (id) => {
  return useQuery({
    queryKey: ["referral", id],
    queryFn: () => getUserReferrals(id),
    placeholderData: keepPreviousData,
  });
};

export const useGetCardTransactionsAdmin = (filters) => {
  return useQuery({
    queryKey: ["cardTransactions", { ...filters }],
    queryFn: () => getCardTransactionsAdmin(filters),
    // placeholderData: keepPreviousData,
  });
};

export const useGetRate = (from: string, to: string = "USDT") => {
  return useQuery({
    queryKey: ["rate", from, to],
    queryFn: () => getRate(from, to),
    enabled: !!from,
  });
};
